/**
 * Partner / customer logo wall content (D-013: content in code, no CMS).
 *
 * IMPORTANT — only list companies BB is actually authorized to display.
 * Showing brands you don't haul for (or without permission) is misleading and a
 * legal risk. The entries below are PLACEHOLDERS so the section renders; replace
 * `name` with real partners and, ideally, add a monochrome logo file.
 *
 * To use a real logo: drop an SVG/PNG at `public/brand/partners/<slug>.svg`
 * (prefer a single-color/dark SVG so it sits cleanly on the light wall) and set
 * `logo: "/brand/partners/<slug>.svg"`. With no `logo`, the `name` renders as a
 * styled wordmark instead.
 */
export interface Partner {
  name: string;
  /** Optional logo asset under /public/brand/partners. Falls back to `name`. */
  logo?: string;
  /**
   * Desktop optical height in px. Tuned per-logo so wide wordmarks and square
   * marks read at a similar visual weight (equal area, not equal height).
   * Scaled down on smaller breakpoints. Defaults to 40 if omitted.
   */
  h?: number;
  /** Hidden from the wall but kept in code so it's easy to switch back on. */
  hidden?: boolean;
}

export const partnersSection = {
  eyebrow: "Who we haul for",
  heading: "Shippers and brokers who trust us with their freight",
  sub: "From energy and chemical producers to the nation's largest freight brokers, these partners count on Bull & Bear to move liquid bulk safely and on time.",
} as const;

const allPartners: Partner[] = [
  { name: "Shell", logo: "/brand/partners/shell.png", h: 62 },
  { name: "TotalEnergies", logo: "/brand/partners/totalenergies.png", h: 56 },
  { name: "BP", logo: "/brand/partners/bp.png", h: 54 },
  { name: "C.H. Robinson", logo: "/brand/partners/ch-robinson.png", h: 50 },
  { name: "TQL", logo: "/brand/partners/tql.png", h: 46 },
  { name: "Uber Freight", logo: "/brand/partners/uber-freight.png", h: 54 },
  // Hidden per management (low volume) — un-hide to restore.
  { name: "RXO", logo: "/brand/partners/rxo.png", h: 44, hidden: true },
  { name: "KAG Logistics", logo: "/brand/partners/kag-logistics.png", h: 48 },
  { name: "Circle Logistics", logo: "/brand/partners/circle-logistics.png", h: 38 },
  { name: "Heniff Logistics", logo: "/brand/partners/heniff.png", h: 40 },
  { name: "Oxyde Chemicals", logo: "/brand/partners/oxyde-chemicals.png", h: 40 },
  // Hidden per management (low volume) — un-hide to restore.
  { name: "Adjuvants Unlimited", logo: "/brand/partners/adjuvants-unlimited.png", h: 52, hidden: true },
  { name: "Tulstar Products", logo: "/brand/partners/tulstar.png", h: 44 },
  // Parked. Re-add if desired:
  // { name: "Penske", logo: "/brand/partners/penske.png" },
];

/** Visible partners (hidden ones stay in `allPartners` for easy restore). */
export const partners: Partner[] = allPartners.filter((p) => !p.hidden);
